import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import * as S from './Profile.styled';
import { memberService, MemberInfo } from 'services/memberService';
import defaultProfileImg from 'assets/MemberImage/defaultProfileImage.png';

function Profile() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<MemberInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await memberService.getMe();
        setUserInfo(data);
      } catch (error) {
        console.error('사용자 정보 조회 실패:', error);
        alert('사용자 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleComplete = async () => {
    if (!profileImage) {
      alert('변경된 프로필 이미지가 없습니다.');
      return;
    }

    setIsUploading(true);
    try {
      // 1. 이미지 업로드
      const uploadResult = await memberService.uploadProfileImage(profileImage);

      // 2. 프로필 이미지 수정 API 호출
      await memberService.updateProfileImage(uploadResult.url);

      alert('프로필 이미지가 수정되었습니다.');

      // 3. 사용자 정보 다시 불러오기
      const updatedInfo = await memberService.getMe();
      setUserInfo(updatedInfo);
      setProfileImage(null);
      setProfileImagePreview('');
    } catch (error) {
      console.error('프로필 이미지 수정 실패:', error);
      alert('프로필 이미지 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <S.Page>
        <S.Header>
          <S.BackButton onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </S.BackButton>
          개인정보 수정
        </S.Header>
        <S.ContentWrap>
          <div>로딩 중...</div>
        </S.ContentWrap>
      </S.Page>
    );
  }

  if (!userInfo) {
    return (
      <S.Page>
        <S.Header>
          <S.BackButton onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </S.BackButton>
          개인정보 수정
        </S.Header>
        <S.ContentWrap>
          <div>사용자 정보를 불러올 수 없습니다.</div>
        </S.ContentWrap>
      </S.Page>
    );
  }

  return (
    <S.Page>
      <S.Header>
        <S.BackButton onClick={handleBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </S.BackButton>
        개인정보 수정
      </S.Header>

      <S.ContentWrap>
        <S.ProfileImageSection>
          <S.ProfileImageContainer>
            <S.ProfileImageWrapper onClick={handleImageClick}>
              <S.ProfileImage
                src={profileImagePreview || userInfo.profileImage || defaultProfileImg}
                alt="프로필 이미지"
              />
            </S.ProfileImageWrapper>
            <S.AddPhotoButton onClick={handleImageClick} type="button" />
          </S.ProfileImageContainer>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
        </S.ProfileImageSection>

        <S.Label>이름</S.Label>
        <S.InfoBox>{userInfo.name}</S.InfoBox>

        <S.Label>학교</S.Label>
        <S.InfoBox>{userInfo.university.name}</S.InfoBox>

        <S.Label>전공</S.Label>
        <S.InfoBox>{userInfo.department}</S.InfoBox>

        <S.Label>학번</S.Label>
        <S.InfoBox>{userInfo.studentId}</S.InfoBox>
      </S.ContentWrap>

      <S.BottomButton onClick={handleComplete} disabled={isUploading || !profileImage}>
        {isUploading ? '저장 중...' : '완료'}
      </S.BottomButton>
    </S.Page>
  );
}

export default Profile;
